import mitt, { type Emitter } from 'mitt';

type WSApiEvent =
  | {
    type: 'ping';
    message: undefined;
  }
  | {
    type: 'order-count';
    message: number;
  }
  | {
    type: 'pending-booking-count';
    message: number;
  }
  | {
    type: 'print-order';
    message: string;
  }
  | {
    type: 'order-status-update';
    message: string;
  }
  | {
    type: 'preferences-update';
    message: undefined;
  };

type WSInternalEvents = {
  type: 'ws-clonnection-closed';
  message: undefined;
};

type WSEvent = WSApiEvent | WSInternalEvents;

type MittEvents = {
  [K in WSEvent['type']]: Extract<WSEvent, { type: K }>['message'];
};

let bus: Emitter<MittEvents>;

const assertCannotReach = (_x: never) => {
  throw new Error('Cannot reach this place in the code');
};

const createWebSocket = async <T extends Emitter<MittEvents>>(
  eventBus: T
): Promise<void> => {
  const endpoint = useBaseURL().replace('http', 'ws');

  const webSocket = new WebSocket(`${endpoint}/ws/`);

  webSocket.onmessage = (event) => {
    const data: WSApiEvent = JSON.parse(event.data);

    switch (data.type) {
      case 'order-status-update':
        eventBus.emit(data.type, data.message);
        break;
      case 'order-count':
        eventBus.emit(data.type, data.message);
        break;
      case 'pending-booking-count':
        eventBus.emit(data.type, data.message);
        break;
      case 'print-order':
        eventBus.emit(data.type, data.message);
        break;
      case 'ping':
        eventBus.emit(data.type);
        break;
      case 'preferences-update':
        eventBus.emit(data.type, data.message);
        break;
      default:
        assertCannotReach(data);
    }
  };

  webSocket.onopen = (evt) => {
    if (!import.meta.dev) {
      return;
    }

    console.info('Socket connection openned', evt);
  };

  webSocket.onerror = (err) => {
    if (import.meta.dev) {
      console.error('Socket encountered error: ', err, 'Closing socket');
    }

    webSocket.close();
  };

  webSocket.onclose = (e) => {
    if (import.meta.dev) {
      console.info('Socket connection was closed', e.reason);
    }

    eventBus.emit('ws-clonnection-closed');
  };
};

const createBus = async (): Promise<Emitter<MittEvents>> => {
  const eventBus = mitt<MittEvents>();

  await createWebSocket(eventBus);

  eventBus.on('ws-clonnection-closed', () => {
    setTimeout(async () => {
      await createWebSocket(eventBus);
    }, 10000);
  });

  return eventBus;
};

export const useWebSocket = async () => {
  if (!bus) {
    bus = await createBus();
  }

  return {
    webSocket: {
      on: bus.on,
      off: bus.off
    }
  };
};
